import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Reveal from "./Reveal";
import { SECTOR_OPTIONS, FUEL_OPTIONS } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyForm = { nombre: "", email: "", telefono: "", cargo: "", empresa: "", sector: "", consumo: "", combustible: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Field = ({ label, children }) => (
  <div>
    <label className="label !text-[0.62rem] mb-2 block">{label}</label>
    {children}
  </div>
);

const LeadForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.empresa || !form.sector) {
      toast({ title: "Faltan datos", description: "Nombre, email, empresa y sector son obligatorios." });
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast({ title: "Email no válido", description: "La dirección de correo no parece correcta." });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast({ title: "Solicitud recibida", description: "Nuestro equipo técnico contactará en breve." });
    } catch {
      toast({ title: "No se ha podido enviar", description: "Error temporal. El formulario puede reenviarse en unos minutos." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="bg-1 dots py-28" data-testid="lead-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <p className="label">Contacto</p>
          <h2 className="font-display text-5xl lg:text-7xl text-white font-semibold mt-5 leading-none">Empecemos.</h2>
          <p className="text-muted mt-7 leading-relaxed max-w-md">
            A partir del consumo térmico de la planta preparamos un estudio de viabilidad sin coste ni compromiso.
          </p>
          <ul className="mt-8 space-y-3 text-white/80 text-[15px]">
            {["Estudio de viabilidad térmica gratuito", "Precio de energía estable a largo plazo", "Energía que elimina CO₂, medible y certificable"].map((f) => (
              <li key={f} className="flex items-center gap-3"><span className="w-2 h-2" style={{ backgroundColor: "var(--green)" }} />{f}</li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={120}>
          {submitted ? (
            <div className="border border-line bg-2 p-10 text-center" data-testid="lead-success">
              <CheckCircle2 className="w-12 h-12 text-green mx-auto" strokeWidth={1.2} />
              <h3 className="font-display text-3xl text-white mt-6">Gracias, {form.nombre.split(" ")[0]}</h3>
              <p className="text-muted mt-3 max-w-sm mx-auto">Solicitud recibida. Nuestro equipo técnico se pondrá en contacto para agendar la auditoría térmica.</p>
              <button onClick={() => { setForm(emptyForm); setSubmitted(false); }} className="link-arrow mt-8 text-sm" data-testid="lead-reset-btn">
                Enviar otra solicitud <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Nombre*"><input className="input-dark" value={form.nombre} onChange={handle("nombre")} placeholder="Nombre y apellidos" data-testid="lead-nombre" /></Field>
                <Field label="Cargo"><input className="input-dark" value={form.cargo} onChange={handle("cargo")} placeholder="Director de planta" data-testid="lead-cargo" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Email*"><input type="email" className="input-dark" value={form.email} onChange={handle("email")} placeholder="nombre@empresa.com" data-testid="lead-email" /></Field>
                <Field label="Teléfono"><input type="tel" className="input-dark" value={form.telefono} onChange={handle("telefono")} placeholder="+34 600 000 000" data-testid="lead-telefono" /></Field>
              </div>
              <Field label="Empresa*"><input className="input-dark" value={form.empresa} onChange={handle("empresa")} placeholder="Nombre de la empresa" data-testid="lead-empresa" /></Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Sector*">
                  <select className="input-dark" value={form.sector} onChange={handle("sector")} data-testid="lead-sector">
                    <option value="">Seleccione…</option>
                    {SECTOR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Combustible actual">
                  <select className="input-dark" value={form.combustible} onChange={handle("combustible")} data-testid="lead-combustible">
                    <option value="">Seleccione…</option>
                    {FUEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Consumo anual aproximado (kWh/año)"><input className="input-dark" value={form.consumo} onChange={handle("consumo")} placeholder="Ej. 12.000.000" data-testid="lead-consumo" /></Field>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5 pt-2">
                <button type="submit" className="btn-pill disabled:opacity-60" disabled={sending} data-testid="lead-submit-btn">
                  {sending ? "Enviando…" : "Solicitar estudio"}
                  <span className="circle"><ArrowRight className="w-4 h-4" /></span>
                </button>
                <p className="text-xs text-muted">Los datos se tratan con confidencialidad y solo para este estudio. Respondemos por email o teléfono.</p>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default LeadForm;
